// Hidden attic page - lost manuscript pages
// Presents fictional "recovered" manuscript pages in a deteriorated, archival style
import hidden from '../../content/hidden.json';
import { glitchText, glitchBurst } from '../../scripts/effects';
import { appendReturnToHouse } from '../../scripts/navigation';

interface ManuscriptPage {
  number: string;
  classification: string;
  content: string;
}

interface AtticContent {
  title: string;
  subtitle: string;
  pages: ManuscriptPage[];
}

// Store interval IDs for cleanup
let glitchIntervals: number[] = [];

/**
 * Render the hidden attic page - Lost Manuscript Pages
 * Features a faded, archival aesthetic with pages that appear damaged/aged
 */
export function renderAttic(): void {
  const app = document.getElementById('app');
  if (!app) return;

  // Clean up any existing intervals
  cleanupAttic();

  app.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'hidden-container attic-container';

  const atticData = (hidden as { attic: AtticContent }).attic;
  const pages = atticData?.pages || [];

  // Render manuscript pages
  const pagesList = pages
    .map(
      (page, index) => `
      <article class="attic-page" data-page-index="${index}" aria-label="Manuscript page ${page.number}, ${page.classification}">
        <header class="attic-page-header">
          <span class="attic-page-number">PAGE ${page.number}</span>
          <span class="attic-page-classification">[${page.classification}]</span>
        </header>
        <div class="attic-page-content">
          <p class="attic-page-text">${page.content}</p>
        </div>
        <div class="attic-page-damage" aria-hidden="true"></div>
      </article>
    `
    )
    .join('<div class="attic-page-divider" aria-hidden="true">· · · · · · · · · ·</div>');

  container.innerHTML = `
    <!--
      ┌──────────────────────────────────────────────────────────────────┐
      │  ATTIC ACCESS GRANTED                                           │
      │                                                                  │
      │  You found the lost manuscript pages.                           │
      │  Water damage. Fire. Deliberate erasure.                        │
      │  Their sequence does not correspond to the published text.      │
      │                                                                  │
      │  Two more hidden rooms exist:                                   │
      │    /basement - AI narrator fragments                            │
      │    /archive  - Sealed BBS threads                               │
      │                                                                  │
      │  From the house, try:                                           │
      │    > "wesleyan" or "carlos" for basement                        │
      │    > "vectors" or "manuscript" for archive                      │
      │    > Triple-click the center for basement                       │
      │    > ↑↑↓↓←→←→ for archive                                       │
      │                                                                  │
      │  Progress: __chopras.status()                                   │
      │  Hint: __chopras.hint()                                         │
      └──────────────────────────────────────────────────────────────────┘
    -->
    <header class="hidden-header attic-header" id="main-content" tabindex="-1" role="banner">
      <div class="attic-title">> ${atticData?.title || 'RECOVERED MANUSCRIPT PAGES'}</div>
      <div class="hidden-subheader attic-subtitle">> ${atticData?.subtitle || 'AUTHENTICITY: DISPUTED'}</div>
      <div class="attic-warning" role="alert">
        <span class="attic-warning-icon" aria-hidden="true">⚠</span>
        <span>DOCUMENT INTEGRITY: COMPROMISED</span>
      </div>
    </header>

    <div class="attic-archive-notice">
      The following pages were recovered from sources that cannot be verified.
      Some appear to be early drafts. Others show signs of water damage, fire,
      or deliberate erasure. Their sequence does not correspond to the published
      manuscript. Handle with appropriate skepticism.
    </div>

    <main class="attic-pages" role="main" aria-label="Recovered manuscript pages">
      ${pagesList}
    </main>

    <footer class="attic-footer">
      <div class="attic-footer-line" aria-hidden="true">────────────────────────────────────────</div>
      <div class="attic-footer-text">
        ARCHIVE STATUS: INCOMPLETE | PAGES REMAINING: ∞
      </div>
    </footer>
  `;

  app.appendChild(container);

  // Add return to house navigation
  appendReturnToHouse(container, { className: 'hidden-nav attic-nav' });

  // Apply periodic glitch effects to page text
  initializeAtticEffects(container);
}

/**
 * Initialize interactive effects for the attic page
 */
function initializeAtticEffects(container: HTMLElement): void {
  const pageElements = container.querySelectorAll('.attic-page');

  pageElements.forEach((page, index) => {
    const textEl = page.querySelector('.attic-page-text') as HTMLElement;
    const pageNum = page.querySelector('.attic-page-number') as HTMLElement;

    if (textEl) {
      // Random glitch on page text at staggered intervals
      const delay = index * 3000 + Math.random() * 2000;
      setTimeout(() => {
        glitchText(textEl, 800);
      }, delay);

      // Set up recurring subtle glitches
      const intervalId = window.setInterval(() => {
        if (Math.random() > 0.7) {
          glitchText(textEl, 400);
        }
      }, 8000 + Math.random() * 4000);
      glitchIntervals.push(intervalId);
    }

    // Apply burst glitch to special pages (∞, 0)
    if (pageNum && (pageNum.textContent?.includes('∞') || pageNum.textContent?.includes('0'))) {
      setTimeout(() => {
        glitchBurst(page as HTMLElement, 2, 5);
      }, 2000 + index * 500);
    }
  });

  // Add hover interaction for pages
  pageElements.forEach((page) => {
    page.addEventListener('mouseenter', () => {
      const textEl = page.querySelector('.attic-page-text') as HTMLElement;
      if (textEl && Math.random() > 0.6) {
        glitchText(textEl, 300);
      }
    });
  });
}

/**
 * Cleanup attic page - clear any running intervals
 */
export function cleanupAttic(): void {
  glitchIntervals.forEach((id) => {
    window.clearInterval(id);
  });
  glitchIntervals = [];
}
